import { Box3, Matrix3, Matrix4, Vector3 } from 'three';
import { CollisionDescriptorType, CollisionDescriptorUsageType } from '../../interfaces/GenericInterfaces';
import { CollisionDescriptorDTO, OBBCollisionDescriptorDTO } from '../DTOs/CollisionDescriptorDTO';
import { TransformationData } from './TransformationData';
import { OBB } from 'three/examples/jsm/math/OBB';
import { SCALAR_SMALLER_BOX } from './Product';

export class CollisionDescriptor {
  public static fromDTO(collisionDescriptorDTO: CollisionDescriptorDTO): CollisionDescriptor | OBBCollisionDescriptor {
    switch (collisionDescriptorDTO.type) {
      case CollisionDescriptorType.OBB: {
        const oBBCollisionDescriptorDTO = collisionDescriptorDTO as OBBCollisionDescriptorDTO;

        let halfSize: Vector3;
        if (
          oBBCollisionDescriptorDTO.halfSize === undefined || // Checks if halfSize itself is not defined
          oBBCollisionDescriptorDTO.halfSize.length < 3 || // Checks if halfSize does not have 3 elements
          oBBCollisionDescriptorDTO.halfSize[0] === undefined || // Checks if the first element is undefined
          oBBCollisionDescriptorDTO.halfSize[1] === undefined || // Checks if the second element is undefined
          oBBCollisionDescriptorDTO.halfSize[2] === undefined // Checks if the third element is undefined
        ) {
          halfSize = new Vector3(0, 1, 0);
        } else {
          halfSize = new Vector3(
            oBBCollisionDescriptorDTO.halfSize[0],
            oBBCollisionDescriptorDTO.halfSize[1],
            oBBCollisionDescriptorDTO.halfSize[2],
          );
        }

        return new OBBCollisionDescriptor(
          collisionDescriptorDTO.id?.toString(),
          collisionDescriptorDTO.type,
          TransformationData.fromDTO(oBBCollisionDescriptorDTO.transformationData),
          halfSize,
          collisionDescriptorDTO.usageTypes,
        );
      }
    }
  }

  protected _id: string | undefined;
  protected _type: CollisionDescriptorType;
  protected _usageTypes: CollisionDescriptorUsageType[];

  public get type(): CollisionDescriptorType {
    return this._type;
  }

  public get usageTypes() {
    return this._usageTypes;
  }

  constructor(
    id: string | undefined,
    type: CollisionDescriptorType,
    usageTypes: CollisionDescriptorUsageType[] = [CollisionDescriptorUsageType.DEFAULT],
  ) {
    if (usageTypes === null) {
      usageTypes = [CollisionDescriptorUsageType.DEFAULT];
    }
    this._id = id;
    this._type = type;
    this._usageTypes = usageTypes;
  }

  public toDTO() {
    return new CollisionDescriptorDTO(Number(this._id), this._type, this._usageTypes);
  }
}

export class OBBCollisionDescriptor extends CollisionDescriptor {
  private _transformationData: TransformationData;
  private _halfSize: Vector3;
  private _obb: OBB;
  private _smallerOBB: OBB;

  public getOBB(): OBB {
    return this._obb;
  }

  public getSmallerOBB(): OBB {
    return this._smallerOBB;
  }

  public get halfSize(): Vector3 {
    return this._halfSize;
  }

  public get transformationData(): TransformationData {
    return this._transformationData;
  }

  constructor(
    id: string | undefined,
    type: CollisionDescriptorType,
    transformationData: TransformationData,
    halfSize: Vector3,
    usageTypes?: CollisionDescriptorUsageType[],
  ) {
    super(id, type, usageTypes);

    this._transformationData = transformationData;
    this._halfSize = halfSize;

    const euler = this._transformationData.rotation.clone();
    const matrix4 = new Matrix4().makeRotationFromEuler(euler);
    const matrix3 = new Matrix3().setFromMatrix4(matrix4);
    const position = this._transformationData.position.clone();
    this._obb = new OBB(position, this._halfSize, matrix3);
    this._smallerOBB = new OBB(position, this._halfSize.clone().multiplyScalar(1 + SCALAR_SMALLER_BOX), matrix3);
  }

  public getFutureOBB(
    targetRotationMatrix: Matrix3,
    targetMatrix: Matrix4,
    productScale: Vector3,
    useSmallerBBox = false,
  ): OBB {
    const obb = useSmallerBBox ? this._smallerOBB.clone() : this._obb.clone();

    // change rotation OBB
    obb.rotation.premultiply(targetRotationMatrix);

    // take scale into account
    obb.halfSize.multiply(productScale);
    obb.center.multiply(productScale);

    // apply full transformation to position
    obb.center.applyMatrix4(targetMatrix);

    return obb;
  }

  public override toDTO() {
    return new OBBCollisionDescriptorDTO(
      Number(this._id),
      this._type,
      this.transformationData.toDTO(),
      this._halfSize ? [this._halfSize.x, this._halfSize.y, this._halfSize.z] : [0, 0, 0],
    );
  }
}

export class Box3CollisionDescriptor extends CollisionDescriptor {
  private _box: Box3;
  private _smallerBox: Box3;

  constructor(
    id: string | undefined,
    type: CollisionDescriptorType,
    box: Box3,
    usageType = [CollisionDescriptorUsageType.DEFAULT],
  ) {
    super(id, type, usageType);

    this._box = box.clone();
    this._smallerBox = box.clone().expandByScalar(1 + SCALAR_SMALLER_BOX);
  }

  public getFutureBox3(matrix: Matrix4, productScale: Vector3, useSmallerBBox = false): Box3 {
    const obb = useSmallerBBox ? this._smallerBox.clone() : this._box.clone();

    obb.applyMatrix4(matrix);

    return obb;
  }
}
